/**
*
* MonitorTypes
*
*/
import { Component } from 'react';
import MonitorImage from '@shared/Resources/Monitor/shared/MonitorImage';
import { MonitorTypesTitle } from '@shared/Resources/Monitor/shared/Titles';
import _ from 'lodash';
import PropTypes from 'prop-types';
import CheckBox from '@components/CheckBox';
import NumericSelector from '@components/NumericSelector';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import Media from '../../../../Media/Client';
import { MONITOR_TYPES } from '../../../types';
import {
  ExtraMonitor, ExtraMonitorsHeader, ExtraMonitorsWrapper, Header,
  InhalationIcon, InhaleContainer,
  LabelContainer,
  MirrorIcon, MonitorImageWrapper, MonitorType,
  PlaybackIcon, PlaybackOption,
  SpeedometerIcon, TypesContainer,
  VideoIcon
} from './styles';
import { DEFAULT_DURATION_SECOND } from '../MonitorPatterns/consts';
import {withTranslation} from "react-i18next";
import {compose} from "redux";
import MonitorPatterns from '../MonitorPatterns';
import { V2_MONITOR_REGISTRY } from '../../../v2Monitors/registry';

class MonitorTypes extends Component {
  constructor(props) {
    super(props);
    const monitorWithDefaults = {
      ...props.monitor,
      playback: props.monitor?.playback || { on: false },
    };
    this.state = {
      monitor: monitorWithDefaults,
    };
  }

  updateMonitor = (monitor) => {
    this.setState({ monitor });
    this.props.onChange(monitor);
  }

  monitorTypeSelected = (type) => () => {
    const { monitor } = this.state;
    const definition = V2_MONITOR_REGISTRY[type];
    const nextMonitor = {
      ...monitor,
      type,
      ...(definition && {
        config: {
          ...definition.defaultConfig,
          ...(monitor.type === type ? monitor.config : {}),
        },
      }),
    };

    if (type === MONITOR_TYPES.BREATHING || type === MONITOR_TYPES.LOUDNESS) {
      nextMonitor.inhalation = {
        ...monitor.inhalation,
        on: true,
        value: DEFAULT_DURATION_SECOND.TWO,
      };
    }

    this.updateMonitor(nextMonitor);
  }

  toggleExtraMonitor = (fieldToggled) => () => {
    const { monitor } = this.state;
    monitor[fieldToggled].on = !monitor[fieldToggled].on;
    this.updateMonitor(monitor);
  }

  updateInhalationDuration = (newDuration) => {
    const { monitor } = this.state;
    monitor.inhalation.value = newDuration;
    this.updateMonitor(monitor);
  }

  togglePlaybackOption = () => {
    const { monitor } = this.state;
    if (!monitor.playback) {
      monitor.playback = { on: false };
    }
    monitor.playback.on = !monitor.playback.on;
    this.updateMonitor(monitor);
  }

  updateVideoUrl = (changedComponent) => {
    const id = changedComponent.value;

    const { monitor } = this.state;
    monitor.video = { on: true, id };
    this.updateMonitor(monitor);
  }

  updateV2Config = (config) => {
    const { monitor } = this.state;
    this.updateMonitor({ ...monitor, config });
  }

  renderMonitorType = (monitorType, definition) => {
    const isSelected = monitorType === this.state.monitor.type;
    const SelectorComponent = definition?.SelectorComponent;

    return (
      <MonitorType isSelected={isSelected} key={monitorType} className="monitorType" onClick={this.monitorTypeSelected(monitorType)}>
        <MonitorImageWrapper>
          {SelectorComponent
            ? <SelectorComponent isSelected={isSelected} />
            : <MonitorImage isSelected={isSelected} type={monitorType} />}
        </MonitorImageWrapper>
        <Text size={TEXT_SIZE.T4} textColor={TEXT_COLOR[isSelected ? 'ACTIVE' : 'NORMAL']}>
          {definition ? this.props.t(definition.titleKey) : _.capitalize(monitorType)}
        </Text>
      </MonitorType>
    );
  }

  renderV2Config = () => {
    const { monitor } = this.state;
    const definition = V2_MONITOR_REGISTRY[monitor.type];
    if (!definition?.BuilderComponent) return null;

    const { BuilderComponent, defaultConfig } = definition;
    return (
      <BuilderComponent
        config={{ ...defaultConfig, ...monitor.config }}
        onChange={this.updateV2Config}
      />
    );
  }

  renderHeader = () => {
    const showRequiredSign = this.state.monitor.type.length === 0;
    return (
      <Header>
        <MonitorTypesTitle showRequiredSign={showRequiredSign} />
      </Header>
    )
  };

  renderExtraMonitor = (monitorClassName, monitorField, Icon, extra = null) => (
    <ExtraMonitor>
      <CheckBox
        value={this.state.monitor[monitorField].on}
        className={monitorClassName}
        onChange={this.toggleExtraMonitor(monitorField)}
        label={
          <LabelContainer>
            <Icon />
            <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H4}>
              {this.props.t(`Common.UI.${monitorClassName}`)}
            </Text>
          </LabelContainer>
        }
      />
      {extra}
    </ExtraMonitor>
    );

  renderMonitorPatterns = () => {
    const { monitorPattern, updatePatternField } = this.props;
    const { monitor } = this.state;
    
    if (!monitorPattern) {
      return null;
    }
    
    return (
      <MonitorPatterns 
        pattern={monitorPattern}
        onChange={updatePatternField}
        monitor={monitor}
        stimulusType={this.props.stimulusType}
        disabledTypes={monitor.type === MONITOR_TYPES.NONE}
      />
    );
  }

  renderPlaybackOption = () => {
    const { monitor } = this.state;

    return (
      <PlaybackOption
        value={monitor.playback?.on || false}
        onChange={this.togglePlaybackOption}
        isDisabled={monitor.type === MONITOR_TYPES.NONE}
        label={
          <LabelContainer>
            <PlaybackIcon />
            <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H4}>
              {this.props.t(`UI.enablePlaybackOption`)}
            </Text>
          </LabelContainer>
        }
      />
    );
  }

  renderExtraMonitors = () => {
    const  {t} = this.props
    const { monitor } = this.state;
    return (
      <div>
        <ExtraMonitorsHeader>
          <Text textColor={TEXT_COLOR.DARK_BLUE} weight="bold">
            {t('UI.extraMonitors')}
          </Text>
          <Text size={TEXT_SIZE.T4} textColor={TEXT_COLOR.LIGHT_GRAY} margin="0 19px">
            {t('Informative.Notifying.extraSelect')}
          </Text>
        </ExtraMonitorsHeader>
        {/** extra features */}
        <ExtraMonitorsWrapper className="extraMonitorTypes">
          {this.renderExtraMonitor('extraMonitorInhale', 'inhalation', InhalationIcon, monitor.inhalation.on &&
            <InhaleContainer>
              <NumericSelector className="extraMonitorInhaleValue" value={monitor.inhalation.value||1} onChange={this.updateInhalationDuration} />
              <Text size={TEXT_SIZE.T4} textColor={TEXT_COLOR.DARK_BLUE} margin="0 8px">
                {t('Dates.secsCount', {
                  count: ''
                })}
              </Text>
            </InhaleContainer>
          )}
          {this.renderExtraMonitor('extraMonitorMirror', 'mirror', MirrorIcon)}
          {this.renderExtraMonitor('extraMonitorSpeedometer', 'speedometer', SpeedometerIcon)}
          {this.renderExtraMonitor('extraMonitorVideo', 'video', VideoIcon)}
          {monitor.video.on &&
            <Media className="extraMonitorVideoValue" onChange={this.updateVideoUrl} payload={{ value: monitor.video.url }} />
          }
        </ExtraMonitorsWrapper>
      </div>
    );
  }

  render() {
    const isV2Monitor = Boolean(V2_MONITOR_REGISTRY[this.state.monitor.type]);
    const isV1Monitor = _.values(MONITOR_TYPES).includes(this.state.monitor.type)
      && !isV2Monitor;
    const v1MonitorTypes = _.values(MONITOR_TYPES)
      .filter((type) => !V2_MONITOR_REGISTRY[type]);

    return (
      <div className="resource-item">
        {this.renderHeader()}
        <Text textColor={TEXT_COLOR.DARK_BLUE} weight="bold">
          {this.props.t('UI.v1Monitors')}
        </Text>
        <TypesContainer className="monitorTypes">
          {v1MonitorTypes.map((type) => this.renderMonitorType(type))}
        </TypesContainer>
        <Text textColor={TEXT_COLOR.DARK_BLUE} weight="bold">
          {this.props.t('UI.v2Monitors')}
        </Text>
        <TypesContainer className="v2MonitorTypes">
          {Object.entries(V2_MONITOR_REGISTRY)
            .map(([type, definition]) => this.renderMonitorType(type, definition))}
        </TypesContainer>
        {this.renderPlaybackOption()}
        {isV2Monitor && this.renderV2Config()}
        {isV1Monitor && (
          <>
            {this.renderMonitorPatterns()}
            {this.renderExtraMonitors()}
          </>
        )}
      </div>);
  }
}


MonitorTypes.propTypes = {
  onChange: PropTypes.func,
  updatePatternField: PropTypes.func,
  monitor: PropTypes.shape({
    type: PropTypes.string,
    inhalation: PropTypes.shape({
      on: PropTypes.bool,
      value: PropTypes.number,
    }),
    mirror: PropTypes.shape({
      on: PropTypes.bool,
    }),
    speedometer: PropTypes.shape({
      on: PropTypes.bool,
    }),
    playback: PropTypes.shape({
      on: PropTypes.bool,
    }),
    config: PropTypes.object,
    video: PropTypes.shape({
      on: PropTypes.bool,
      id: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  monitorPattern: PropTypes.shape({
    type: PropTypes.string,
    productionDuration: PropTypes.number,
    selfManagePace: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
  }),
  stimulusType: PropTypes.object,
  t: PropTypes.func,
};

export default compose(
    withTranslation()
)( MonitorTypes);
